import React from 'react';
import {
  chipStyle,
  chipWrapStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
  summaryGridStyle,
  summaryLabelStyle,
  summaryValueStyle,
} from './dashboardStyles';
import { statusPillStyle } from './CompanyModuleRuntimeGroups';

const SCOPE_LABELS = {
  tenant_shared: 'tenant 공용',
  company_single: '회사 단일',
  company_shared_sales: '회사+공용 sales',
  internal_only: '내부 전용',
  platform_only: '플랫폼 운영',
};

const DEFAULT_LABELS = {
  include: '새 회사 자동 포함',
  exclude: '새 회사 기본 제외',
  manual: '새 회사 수동 선택',
};

const STATUS_LABELS = {
  ready: '준비됨',
  partial: '부분 준비',
  needs_company_code: '회사코드 필요',
  global_only: '전역만 있음',
  not_ready: '미준비',
  blocked: '배정 차단',
  pine_only: 'PINE 전용',
  platform_only: '운영자 전용',
  review: '검토 필요',
};

function statusTone(value) {
  if (['ready'].includes(value)) return 'ok';
  if (['partial', 'global_only', 'review', 'pine_only', 'platform_only'].includes(value)) return 'warning';
  return 'danger';
}

function SummaryCard({ label, value, help }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value ?? '-'}</div>
      {help ? <div style={{ ...rowMetaStyle, marginTop: 4 }}>{help}</div> : null}
    </div>
  );
}

function PolicyCard({ policy, companyRows }) {
  const rows = companyRows.filter((row) => row.moduleCode === policy.moduleCode);
  const included = rows.filter((row) => row.isIncluded);
  const excluded = rows.filter((row) => !row.isIncluded);
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 10,
        background: 'var(--bg-card)',
        display: 'grid',
        gap: 7,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={rowTitleStyle}>{policy.moduleName || policy.moduleCode}</div>
          <div style={rowMetaStyle}>{policy.moduleCode}</div>
        </div>
        <span style={statusPillStyle(policy.assignmentStatus === 'ready' ? 'ok' : statusTone(policy.assignmentStatus))}>
          {SCOPE_LABELS[policy.scopeType] || policy.scopeType}
        </span>
      </div>
      <div style={chipWrapStyle}>
        <span style={chipStyle}>{DEFAULT_LABELS[policy.newCompanyDefault] || policy.newCompanyDefault}</span>
        <span style={chipStyle}>포함 {included.length}</span>
        <span style={chipStyle}>제외 {excluded.length}</span>
        {policy.requiresSharedSalesRoster ? <span style={chipStyle}>sales 원장 연결</span> : null}
        {policy.supportsMultiCompanyScope ? <span style={chipStyle}>여러 회사 포함 가능</span> : null}
      </div>
      <div style={chipWrapStyle}>
        <span style={statusPillStyle(statusTone(policy.dataScopeStatus))}>데이터 {STATUS_LABELS[policy.dataScopeStatus] || policy.dataScopeStatus}</span>
        <span style={statusPillStyle(statusTone(policy.menuStatus))}>메뉴 {STATUS_LABELS[policy.menuStatus] || policy.menuStatus}</span>
        <span style={statusPillStyle(statusTone(policy.apiScopeStatus))}>API {STATUS_LABELS[policy.apiScopeStatus] || policy.apiScopeStatus}</span>
        <span style={statusPillStyle(statusTone(policy.assignmentStatus))}>배정 {STATUS_LABELS[policy.assignmentStatus] || policy.assignmentStatus}</span>
      </div>
      <div style={rowMetaStyle}>
        포함: {included.map((row) => row.companyName || row.companyCode).join(', ') || '-'}
      </div>
      {excluded.length ? (
        <div style={rowMetaStyle}>
          제외: {excluded.map((row) => row.companyName || row.companyCode).join(', ')}
        </div>
      ) : null}
      {policy.policyNote ? <div style={rowMetaStyle}>{policy.policyNote}</div> : null}
    </div>
  );
}

function IssueList({ issues }) {
  if (!issues.length) {
    return (
      <div style={{ ...summaryCardStyle, borderColor: 'rgba(22,163,74,0.35)', background: 'rgba(22,163,74,0.08)' }}>
        <div style={rowTitleStyle}>포함 회사 정책에서 즉시 위험한 충돌은 없습니다.</div>
        <div style={rowMetaStyle}>아직 읽기 전용 진단이며 실제 메뉴/권한에는 영향을 주지 않습니다.</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {issues.slice(0, 10).map((issue, idx) => (
        <div
          key={`${issue.type}:${issue.companyCode}:${issue.moduleCode}:${idx}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: 8,
            border: '1px solid var(--border)',
            borderRadius: 7,
            padding: '8px 9px',
            background: issue.severity === 'danger' ? 'rgba(220,38,38,0.08)' : 'rgba(217,119,6,0.08)',
          }}
        >
          <div>
            <div style={rowTitleStyle}>
              {issue.companyName || issue.companyCode} / {issue.moduleName || issue.moduleCode}
            </div>
            <div style={rowMetaStyle}>{issue.message}</div>
          </div>
          <span style={statusPillStyle(issue.severity === 'danger' ? 'danger' : issue.severity === 'warning' ? 'warning' : 'info')}>
            {issue.type}
          </span>
        </div>
      ))}
      {issues.length > 10 ? <div style={rowMetaStyle}>외 {issues.length - 10}건이 더 있습니다.</div> : null}
    </div>
  );
}

export default function CompanyModuleScopePolicyPanel({ report }) {
  if (!report) return null;
  if (!report.ready) {
    return (
      <div style={{ ...summaryCardStyle, padding: 12 }}>
        <h3 style={panelTitleStyle}>모듈별 포함 회사 설정</h3>
        <div style={panelSubStyle}>{report.message || '범위 정책 테이블이 아직 준비되지 않았습니다.'}</div>
      </div>
    );
  }

  const summary = report.summary || {};
  const policies = report.policies || [];
  const companyRows = report.companyRows || [];
  const issues = report.issues || [];
  const importantPolicies = policies
    .filter((policy) => (
      policy.requiresSharedSalesRoster
      || policy.scopeType === 'tenant_shared'
      || policy.scopeType === 'internal_only'
      || policy.assignmentStatus !== 'ready'
      || policy.dataScopeStatus !== 'ready'
      || policy.apiScopeStatus !== 'ready'
      || policy.menuStatus !== 'ready'
    ))
    .slice(0, 12);

  return (
    <section style={{ ...summaryCardStyle, padding: 12 }}>
      <div>
        <h3 style={panelTitleStyle}>모듈별 포함 회사 설정</h3>
        <div style={panelSubStyle}>
          새 회사가 추가됐을 때 어떤 모듈에 포함할지, 연결을 끊을 수 있는지, 데이터/API/메뉴가 안전한지 보는 읽기 전용 정책표입니다.
        </div>
      </div>

      <div style={{ ...summaryGridStyle, marginTop: 10 }}>
        <SummaryCard label="정책 모듈" value={summary.policyCount} help="SaaS 모듈 범위 정책" />
        <SummaryCard label="포함 연결" value={summary.includedCount} help="tenant-company-module 포함 수" />
        <SummaryCard label="tenant 공용" value={summary.tenantSharedCount} help="회사 여러 개를 함께 볼 수 있음" />
        <SummaryCard label="공용 sales 연결" value={summary.sharedSalesCount} help="마케팅명부/영업자 원장 연결 필요" />
        <SummaryCard label="내부 전용" value={summary.internalOnlyCount} help="PINE 내부 운영 우선" />
        <SummaryCard label="보강 필요" value={summary.needsWorkCount} help={`위험 ${summary.dangerCount || 0} · 확인 ${summary.warningCount || 0}`} />
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ ...rowTitleStyle, marginBottom: 6 }}>정책 확인 필요</div>
        <IssueList issues={issues} />
      </div>

      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
        {importantPolicies.map((policy) => (
          <PolicyCard key={policy.moduleCode} policy={policy} companyRows={companyRows} />
        ))}
      </div>
    </section>
  );
}
